/* eslint-disable max-lines-per-function */
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useSupabaseClient } from '@/hooks/useSupabaseClient';
import { getSubscriptionPreferences } from '@/lib/storageKeys';
import { StripeSubscriptionStatus } from '@/types/stripe';
import {
  Subscription,
  SubscriptionBillingPeriod,
  SubscriptionTier,
} from '@/types/subscription';

const isSubscribedStatus = (status: StripeSubscriptionStatus) => {
  return status === 'active' || status === 'trialing';
};

interface SubscriptionContextType {
  subscription: Subscription | null;
  isLoading: boolean;
  isTeamSubscriber: boolean;
  isProSubscriber: boolean;
  isSubscribed: boolean;
  refreshSubscription: () => Promise<void>;
  upgradeSubscription: () => Promise<void>;
  createCheckoutSession: () => Promise<void>;
  openCustomerPortal: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined
);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider = ({
  children,
}: Readonly<SubscriptionProviderProps>) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { user, isLoaded } = useAuth();
  const { toast } = useToast();
  const supabase = useSupabaseClient();

  const isTeamSubscriber = subscription?.tier === SubscriptionTier.Team;
  const isProSubscriber = subscription?.tier === SubscriptionTier.Pro;
  const isSubscribed = subscription
    ? isSubscribedStatus(subscription.status)
    : false;

  const initialLoadDone = useRef(false);

  const refreshSubscription = useCallback(async () => {
    try {
      if (!initialLoadDone.current) {
        setIsLoading(true);
      }

      if (!isLoaded) {
        setIsLoading(false);
        return;
      }

      if (!user?.id) {
        setSubscription(null);
        setIsLoading(false);
        return;
      }

      const { data: dbSubscriptionData, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching subscription from database:', error);
        setSubscription(null);
        return;
      }

      if (dbSubscriptionData) {
        const subscriptionData: Subscription = {
          status: dbSubscriptionData.status as StripeSubscriptionStatus,
          tier: dbSubscriptionData.tier as SubscriptionTier,
          billing_period:
            dbSubscriptionData.billing_period as SubscriptionBillingPeriod,
          trial_end: dbSubscriptionData.trial_end,
          current_period_end: dbSubscriptionData.current_period_end,
          cancel_at_period_end:
            dbSubscriptionData.cancel_at_period_end || false,
          canceled_at: dbSubscriptionData.canceled_at,
          seats: dbSubscriptionData.seats,
        };

        setSubscription(subscriptionData);
      } else {
        setSubscription(null);
      }
    } catch (error) {
      console.error('Error in refreshSubscription:', error);
      setSubscription(null);
    } finally {
      initialLoadDone.current = true;
      setIsLoading(false);
    }
  }, [supabase, user?.id, isLoaded]);

  const upgradeSubscription = useCallback(async () => {
    if (!user?.id) {
      toast({
        title: 'Authentication Error',
        description: 'Please log in to upgrade your subscription',
        variant: 'destructive',
      });

      return;
    }

    const { selectedTier, selectedBillingPeriod } =
      getSubscriptionPreferences();

    const { data, error } = await supabase.functions.invoke(
      'stripe/checkout/sessions/upgrade',
      {
        body: { tier: selectedTier, billingPeriod: selectedBillingPeriod },
      }
    );

    if (error) {
      throw new Error(`Request failed: ${error.message}`);
    }

    if (data?.checkout_url) {
      window.location.href = data.checkout_url;
    } else if (data?.success) {
      toast({
        title: 'Subscription Upgraded!',
        description: `Your ${selectedTier} subscription is now active. Welcome to ${selectedTier}!`,
      });

      await refreshSubscription();
    }

    return data;
  }, [user?.id, supabase, toast, refreshSubscription]);

  const createCheckoutSession = useCallback(
    async () => {
      const { userCurrency, selectedTier, selectedBillingPeriod } =
        getSubscriptionPreferences();

      if (!user?.id) {
        throw new Error(
          'User ID is required to create a team checkout session'
        );
      }

      const { data, error } = await supabase.functions.invoke(
        'stripe/checkout/sessions/subscribe',
        {
          body: {
            user_id: user.id,
            tier: selectedTier,
            billingPeriod: selectedBillingPeriod,
            currency: userCurrency,
          },
        }
      );

      if (error) {
        console.error('Error creating Team checkout session:', error);

        toast({
          title: 'Error creating subscription',
          description: 'Please try again or contact support.',
          variant: 'destructive',
        });

        return;
      }

      if (!data?.checkout_url) {
        toast({
          title: 'Error',
          description: 'Unable to start checkout. Please try again.',
          variant: 'destructive',
        });

        return;
      }

      window.location.href = data.checkout_url;
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [user?.id, supabase]
  );

  const openCustomerPortal = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke(
      'stripe/billing-portal/sessions/create'
    );

    if (error) {
      throw new Error(`Request failed: ${error.message}`);
    }

    if (data?.url) {
      window.open(data.url, '_blank');
    }
  }, [supabase]);

  useEffect(() => {
    if (isLoaded) {
      refreshSubscription();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isLoaded, user]);

  const value = useMemo(
    () => ({
      subscription,
      isLoading,
      isTeamSubscriber,
      isProSubscriber,
      isSubscribed,
      refreshSubscription,
      upgradeSubscription,
      createCheckoutSession,
      openCustomerPortal,
    }),
    [
      subscription,
      isLoading,
      isTeamSubscriber,
      isProSubscriber,
      isSubscribed,
      refreshSubscription,
      upgradeSubscription,
      createCheckoutSession,
      openCustomerPortal,
    ]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      'useSubscription must be used within a SubscriptionProvider'
    );
  }
  return context;
};
